/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { apiClient } from '../client'
import type { UserProfile } from '@/types/auth.types'
import type { PaginatedResponse } from '@/types/api.types'
import type { Role } from './roles.api'

export interface CreateUserRequest {
  email: string
  password: string
  firstName: string
  lastName: string
  avatar?: string | null
  phone?: string | null
  isActive?: boolean
  roleIds: string[]
}

export interface UpdateUserRequest {
  email?: string
  firstName?: string
  lastName?: string
  avatar?: string | null
  phone?: string | null
  isActive?: boolean
  password?: string
  roleIds?: string[]
}

export type UserSortField = 'email' | 'firstName' | 'lastName' | 'isActive'
export type SortOrder = 'ASC' | 'DESC'

export interface UserFilters {
  email?: string
  firstName?: string
  isActive?: boolean
  sortBy?: UserSortField
  sortOrder?: SortOrder
  page?: number
  perPage?: number
}

/**
 * Získání seznamu uživatelů s filtrováním a řazením
 */
export const getUsers = async (filters?: UserFilters): Promise<PaginatedResponse<UserProfile>> => {
  const params = new URLSearchParams()

  if (filters?.page) params.append('page', filters.page.toString())
  if (filters?.perPage) params.append('perPage', filters.perPage.toString())
  if (filters?.email) params.append('email', filters.email)
  if (filters?.firstName) params.append('firstName', filters.firstName)
  if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString())
  if (filters?.sortBy) params.append('sortBy', filters.sortBy)
  if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder)

  const response = await apiClient.get<PaginatedResponse<UserProfile>>(
    `/api/users?${params.toString()}`
  )
  return response.data
}

/**
 * Získání detailu uživatele
 */
export const getUser = async (id: string): Promise<UserProfile> => {
  const response = await apiClient.get<UserProfile>(`/api/users/${id}`)
  return response.data
}

/**
 * Vytvoření nového uživatele
 */
export const createUser = async (data: CreateUserRequest): Promise<UserProfile> => {
  const response = await apiClient.post<UserProfile>('/api/users', data)
  return response.data
}

/**
 * Aktualizace uživatele
 */
export const updateUser = async (id: string, data: UpdateUserRequest): Promise<UserProfile> => {
  const response = await apiClient.patch<UserProfile>(`/api/users/${id}`, data)
  return response.data
}

/**
 * Smazání uživatele
 */
export const deleteUser = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/users/${id}`)
}

/**
 * Získání seznamu rolí
 */
export const getRoles = async (): Promise<Role[]> => {
  const response = await apiClient.get<{ success: boolean; data: Role[] }>('/api/users/roles')
  return response.data.data
}
